import React, { FC } from "react"
import { Alert, Checkbox, Space, Typography } from "antd"
import { CheckboxValueType } from "antd/lib/checkbox/Group"
import { useTranslation } from "react-i18next"
import { formatTimeSlotLabel, InterestTimeSlotLike, setSelectedTimeSlots, sortTimeSlots, toggleAllTimes } from "./timeSlotSelection"

export type TimeSlotSelectionValue = {
    allTimesPossible: boolean
    selectedTimeSlotIds: Array<string>
}

type InterestTimeSlotSelectProps = {
    timeSlots: ReadonlyArray<InterestTimeSlotLike>
    value: TimeSlotSelectionValue
    onChange: (value: TimeSlotSelectionValue) => void
    // Validierungshinweis erst nach Absende-Versuch zeigen.
    showError?: boolean
}

const { Title } = Typography

// Zeitfenster-Mehrfachauswahl mit exklusiver „Ich kann zu allen Zeiten"-Option.
// Die eigentliche Zustandslogik liegt in timeSlotSelection.ts (dort getestet).
const InterestTimeSlotSelect: FC<InterestTimeSlotSelectProps> = ({ timeSlots, value, onChange, showError }) => {
    const { t } = useTranslation()
    const sorted = sortTimeSlots(timeSlots)

    const handleGroupChange = (checked: Array<CheckboxValueType>) => {
        onChange(setSelectedTimeSlots(checked.map((v) => String(v))))
    }

    const handleAllTimesChange = (checked: boolean) => {
        onChange(toggleAllTimes(checked, value.selectedTimeSlotIds))
    }

    const isInvalid = Boolean(showError) && !value.allTimesPossible && value.selectedTimeSlotIds.length === 0

    return (
        <Space direction='vertical' size='small' style={{ width: "100%" }} data-testid='interest-timeslots'>
            <Title level={5} style={{ marginBottom: 0 }}>
                {t("interest.timeSlots.title", "Mögliche Zeiten")}
            </Title>
            <Typography.Text type='secondary'>{t("interest.timeSlots.hint", "Wähle alle Zeiten aus, zu denen du kannst.")}</Typography.Text>

            <Checkbox.Group
                value={value.selectedTimeSlotIds}
                onChange={handleGroupChange}
                disabled={value.allTimesPossible}
                data-testid='interest-timeslot-group'
            >
                <Space direction='vertical' size={4}>
                    {sorted.map((slot) => (
                        <Checkbox key={slot.id} value={slot.id}>
                            {formatTimeSlotLabel(slot)}
                        </Checkbox>
                    ))}
                </Space>
            </Checkbox.Group>

            <Checkbox
                checked={value.allTimesPossible}
                onChange={(e) => handleAllTimesChange(e.target.checked)}
                data-testid='interest-alltimes'
            >
                {t("interest.timeSlots.allTimes", "Ich kann zu allen Zeiten")}
            </Checkbox>

            {isInvalid && <Alert type='error' showIcon message={t("interest.timeSlots.required", "Bitte wähle mindestens ein Zeitfenster oder „Ich kann zu allen Zeiten“.")} />}
        </Space>
    )
}

export default InterestTimeSlotSelect
